import React, { useState } from 'react';
import { cn } from '../../lib/utils';
import { Input } from './input';

/**
 * PasswordInput - Reusable password input with visibility toggle
 *
 * Features:
 * - Eye icon to toggle password visibility
 * - Forwards all Input props (className, placeholder, etc.)
 * - Accessible with keyboard (button is focusable)
 * - Uses existing FontAwesome icons (fa-eye, fa-eye-slash)
 *
 * Usage:
 * <PasswordInput
 *   value={password}
 *   onChange={(e) => setPassword(e.target.value)}
 *   placeholder="Enter password"
 *   required
 * />
 */
export const PasswordInput = React.forwardRef(
  ({ className, ...props }, ref) => {
    const [showPassword, setShowPassword] = useState(false);

    const toggleVisibility = () => {
      setShowPassword(!showPassword);
    };

    return (
      <div className="relative">
        <Input
          type={showPassword ? "text" : "password"}
          className={cn("pr-10", className)}
          ref={ref}
          {...props}
        />
        <button
          type="button"
          onClick={toggleVisibility}
          onMouseDown={(e) => e.preventDefault()} // Prevent focus loss on password field
          className="absolute right-0 top-0 h-full px-3 text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-0 focus:ring-indigo-500 rounded-r-md transition-colors z-10 cursor-pointer pointer-events-auto"
          aria-label={showPassword ? "Hide password" : "Show password"}
          tabIndex={0}
        >
          <i
            className={`fas fa-${showPassword ? 'eye-slash' : 'eye'}`}
            aria-hidden="true"
          ></i>
        </button>
      </div>
    );
  }
);

PasswordInput.displayName = "PasswordInput";
